import { AsyncPipe } from '@angular/common';
import { Component, Input, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { TranslateModule } from '@ngx-translate/core';
import { GalaxyAvatarModule } from '@vendasta/galaxy/avatar';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { GalaxyLoadingSpinnerModule } from '@vendasta/galaxy/loading-spinner';
import { GalaxyPopoverModule, PopoverPositions } from '@vendasta/galaxy/popover';
import { ConversationDetail } from '../../../../core/src/lib/interface/conversation.interface';
import { AssignableUser, ConversationAssignService } from './inbox-conversation-assign-picker.service';

@Component({
  selector: 'inbox-conversation-assign-picker',
  templateUrl: './inbox-conversation-assign-picker.component.html',
  styleUrls: ['./inbox-conversation-assign-picker.component.scss'],
  providers: [ConversationAssignService],
  imports: [
    AsyncPipe,
    TranslateModule,
    ReactiveFormsModule,
    MatAutocompleteModule,
    MatButtonModule,
    MatIconModule,
    MatInputModule,
    GalaxyAvatarModule,
    GalaxyFormFieldModule,
    GalaxyLoadingSpinnerModule,
    GalaxyPopoverModule,
  ],
})
export class InboxConversationAssignPickerComponent {
  readonly assignService = inject(ConversationAssignService);

  readonly selectedUser = this.assignService.selectedUser;
  readonly assigning = this.assignService.assigning;
  readonly resolvingName = this.assignService.resolvingName;
  readonly $assigneeState = this.assignService.$assigneeState;
  protected readonly $canAssignToMe = this.assignService.$canAssignToMe;
  protected readonly $loadingUsers = this.assignService.$loadingUsers;
  protected readonly $usersLoadFailed = this.assignService.$usersLoadFailed;
  readonly users$ = this.assignService.users$;

  readonly searchControl = new FormControl('', { nonNullable: true });
  protected readonly popoverPositions = [
    { ...PopoverPositions.BottomRight, offsetY: 8 },
    { ...PopoverPositions.TopRight, offsetY: -8 },
  ];
  private conversationId: string | null = null;

  displayUser = (user: AssignableUser | string | null): string => {
    if (!user) return '';
    if (typeof user === 'string') return user;
    return user.displayName;
  };

  @Input() set conversationDetail(details: ConversationDetail | null) {
    const conversationId = details?.conversation?.conversationId ?? null;
    if (conversationId !== this.conversationId) {
      this.conversationId = conversationId;
      this.searchControl.setValue('', { emitEvent: false });
      this.assignService.setSearchTerm('');
    }
    this.assignService.setConversationDetail(details);
  }

  constructor() {
    this.searchControl.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe((term) => this.assignService.setSearchTerm(typeof term === 'string' ? term : ''));
  }

  onUserSelected(event: MatAutocompleteSelectedEvent): void {
    if (this.assigning()) return;
    this.assignService.assignUser(event.option.value as AssignableUser | null);
  }

  assignToMe(): void {
    this.assignService.assignToMe();
  }

  protected retryFailedUsersLoad(): void {
    this.assignService.retryFailedUsersLoad();
  }

  assignUser(user: AssignableUser | null): void {
    this.assignService.assignUser(user);
  }
}
